/**
 * Tracking Page Route
 * Displays tracking information for a specific tracking number
 */

import { lazy, Suspense, useEffect } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { fetchTracking } from '@/lib/api-client';
import { trackTrackingViewed, trackTrackingNotFound } from '@/lib/analytics-client';
import { TrackingPageSkeleton } from '@/components/ui/Skeleton';

export const Route = createFileRoute('/track/$trackingNumber')({
  component: TrackingPage,
});

const TrackingDisplay = lazy(() => import('@/components/tracking/TrackingDisplay'));

// Demo/mock tracking data for testing visualization
const DEMO_TRACKING_INFO = {
  status: 'in_transit',
  estimatedDeliveryDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
  carrier: 'UPS',
  currentLocation: 'Columbus, OH',
  origin: { city: 'Virginia Beach', state: 'VA' },
  destination: { city: 'Loveland', state: 'OH' },
  events: [
    { status: 'in_transit', message: 'In Transit', location: 'Columbus, OH', datetime: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString() },
    { status: 'in_transit', message: 'Departure Scan', location: 'Richmond, VA', datetime: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString() },
    { status: 'in_transit', message: 'Origin Scan', location: 'Virginia Beach, VA', datetime: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString() },
    { status: 'pre_transit', message: 'Shipment information sent to UPS', location: 'Virginia Beach, VA', datetime: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString() },
  ],
};

function buildTrackingSupportHref(trackingNumber: string) {
  const subject = encodeURIComponent(`Tracking help for ${trackingNumber}`);
  const body = encodeURIComponent(
    `Hi Fathom Coffee,\n\nI need help with tracking number ${trackingNumber}.\n\nThanks!`
  );
  return `mailto:hello@fathomcoffee.com?subject=${subject}&body=${body}`;
}

function safeDecodeTrackingNumber(trackingNumber: string) {
  try {
    return decodeURIComponent(trackingNumber);
  } catch {
    return trackingNumber;
  }
}

function TrackingPage() {
  const { trackingNumber } = Route.useParams();
  const decodedTrackingNumber = safeDecodeTrackingNumber(trackingNumber);
  const supportHref = buildTrackingSupportHref(decodedTrackingNumber);

  // Use demo data for testing when tracking number is "demo"
  const isDemo = import.meta.env.DEV && decodedTrackingNumber.toLowerCase() === 'demo';

  const {
    data: trackingInfo,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['tracking', decodedTrackingNumber],
    queryFn: () => isDemo ? Promise.resolve(DEMO_TRACKING_INFO) : fetchTracking(decodedTrackingNumber),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    retry: 1,
  });

  // Update document title and tracking-specific OG meta tags
  useEffect(() => {
    const title = `Tracking ${decodedTrackingNumber} - Fathom Coffee`;
    document.title = title;

    const description = trackingInfo
      ? `Fathom Coffee shipment ${decodedTrackingNumber} — status: ${trackingInfo.status.replace(/_/g, ' ')}.`
      : `Track Fathom Coffee shipment ${decodedTrackingNumber}.`;

    const setMeta = (key: string, content: string, attr: 'name' | 'property' = 'property') => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('og:title', title);
    setMeta('og:description', description);
    setMeta('description', description, 'name');
  }, [decodedTrackingNumber, trackingInfo]);

  // Track analytics events
  useEffect(() => {
    if (isLoading) return;

    if (trackingInfo && !error) {
      trackTrackingViewed({
        trackingNumber: decodedTrackingNumber,
        carrier: trackingInfo.carrier || null,
        status: trackingInfo.status,
        hasEstimatedDelivery: !!trackingInfo.estimatedDeliveryDate,
      });
    } else if (error || !trackingInfo) {
      trackTrackingNotFound(decodedTrackingNumber);
    }
  }, [trackingInfo, error, isLoading, decodedTrackingNumber]);

  if (isLoading) {
    return (
      <div role="status" aria-live="polite" aria-busy="true">
        <span className="sr-only">Loading tracking information for {decodedTrackingNumber}…</span>
        <TrackingPageSkeleton />
      </div>
    );
  }

  if (error || !trackingInfo) {
    const isNotFound = !error;
    const title = error ? 'Tracking is Temporarily Unavailable' : 'Tracking Number Not Found';
    const guidance = error
      ? 'Try again in a moment. If the issue keeps happening, support can check the shipment directly.'
      : 'Double-check the spelling against your shipping email or order receipt — tracking numbers are case- and character-sensitive — then try again.';

    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <div
          role="alert"
          className="bg-card rounded-lg shadow-lg p-8 border border-border theme-transition"
        >
          <div className="text-6xl mb-4">📦</div>
          <h2 className="text-2xl font-heading font-bold text-foreground mb-4">
            {title}
          </h2>
          <p className="text-foreground-muted mb-2">
            {error
              ? 'We were not able to load tracking details for:'
              : "We couldn't find a shipment with tracking number:"}
          </p>
          <p className="font-mono text-lg font-bold text-foreground mb-6">
            {decodedTrackingNumber}
          </p>
          <p className="text-sm text-foreground-muted mb-6">
            {guidance}
          </p>

          {isNotFound && (
            <div className="mb-6 rounded-lg border border-fathom-gold/30 bg-fathom-gold/5 p-4 text-left">
              <p className="text-sm font-medium text-foreground mb-2">
                Can&rsquo;t find it? Try looking up your order instead.
              </p>
              <p className="text-xs text-foreground-muted mb-3">
                Use the email and order number from your confirmation email — we&rsquo;ll surface every shipment on the order.
              </p>
              <Link
                to="/lookup"
                className="inline-flex items-center justify-center px-5 py-2.5 bg-fathom-blue text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
              >
                Look Up Your Order
              </Link>
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center justify-center px-6 py-3 bg-fathom-gold text-white font-medium rounded-lg hover:bg-fathom-gold-light transition-colors"
            >
              Enter Another Tracking Number
            </Link>
            <a
              href={supportHref}
              className="inline-flex items-center justify-center px-6 py-3 rounded-lg border border-border text-foreground font-medium hover:bg-background-tertiary transition-colors"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Suspense
      fallback={
        <div className="max-w-2xl mx-auto text-center py-16" role="status" aria-live="polite" aria-busy="true">
          <Loader2 className="w-8 h-8 animate-spin text-fathom-gold mx-auto mb-4" />
          <p className="text-foreground-muted">
            Loading tracking details
            <span className="sr-only"> for {decodedTrackingNumber}</span>
            ...
          </p>
        </div>
      }
    >
      <TrackingDisplay
        trackingInfo={trackingInfo}
        trackingNumber={decodedTrackingNumber}
      />
    </Suspense>
  );
}
